/**
 * The recovery state machine (docs/web-runtime-recovery-system.md §7).
 *
 *   VISIBLE ──hidden/freeze──> SUSPENDED ──visible/resume/pageshow──> ASSESSING
 *                                                                      ├─ healthy ─> VISIBLE
 *                                                                      └─ unhealthy ─> LEVEL_1
 *   LEVEL_1 ├─ recovered ─────────────────────────────────────────> VISIBLE
 *           └─ failed/severe ─> LEVEL_2 ├─ recovered ──────────────> VISIBLE
 *                                       └─ still unhealthy + safe ─> LEVEL_3_RELOAD
 *
 * Level 2 and Level 3 are fully implemented but gated off by default —
 * ENABLE_LEVEL_2 / ENABLE_LEVEL_3 in health-policy.ts. Level 2 only removes
 * inactive queries and flushes already-zero-ref-count market-data cache
 * entries; it deliberately does not force-unmount live widgets or remount
 * TradingView (see runLevel2's doc comment for why).
 *
 * Level 3 reloads IMMEDIATELY once ASSESSING decides it's warranted — no
 * countdown, no confirmation UI. This is deliberate: the whole premise for
 * Level 3 is a long-absence return (docs §4/shouldForceLevel1's longAbsence),
 * meaning nothing was actively in flight while the tab was away, so there is
 * nothing for a countdown to protect against. A visible "reloading in 5…"
 * prompt would only interrupt someone who is ACTIVELY using the tab — which
 * this state machine never reaches, since a cycle only fires on a return-type
 * event to begin with, not while the tab has stayed continuously focused.
 * The one remaining check right before reload() is a fresh safeToReload
 * re-check — see runLevel3.
 *
 * A long absence (≥1hr hidden) OR a first-pass score already in Level 3
 * territory additionally short-circuits straight to LEVEL_3_RELOAD from
 * ASSESSING, skipping LEVEL_1/LEVEL_2 entirely — so the user never gets a
 * window to click into a page that's been stale that long, and a cycle that
 * was always going to end in a reload doesn't waste a socket restart getting
 * there first. See the fast path at the top of runCycle.
 *
 * Created per-mount, never a module singleton: every timer, observer and
 * in-flight recovery has to be cancellable when the owning component unmounts.
 */
import type { QueryClient } from '@tanstack/react-query'

import { env } from '@/env'
import store from '@/lib/configs/store-config'
import { isTrackingActive, recordTrackerEvent } from '@/lib/debug/widget-resource-tracker'
import { connectionStatusAtom } from '@/state/connection'
import { debugModeAtom } from '@/state/debug'
import { flushMarketDataCleanup } from '@/state/market-data-cache'

import {
  ENABLE_LEVEL_2,
  ENABLE_LEVEL_3,
  GLOBAL_VISIBILITY_RESYNC_THRESHOLD_MS,
  RECOVERY_DEDUPE_MS,
  RESYNC_ACK_TIMEOUT_MS,
  shouldHoldForDebug,
} from './health-policy'
import {
  assess,
  createSignalCollector,
  getReloadBlockersNow,
  shouldForceLevel1,
} from './health-signals'
import { flushAllPendingWidgetConfigs } from './pending-widget-config-registry'
import { shouldInvalidateForRuntimeRecovery } from './recovery-query-policy'
import { environmentFields, reportLevel3Outcome, reportRecovery } from './recovery-telemetry'
import {
  canAttemptReload,
  clearBackendStreamFailure,
  isBackendStreamFailure,
  isBackendStreamReloadSuppressed,
  recordBackendStreamFailure,
  recordReloadAttempt,
} from './reload-guard'
import { beginResync, waitForResyncAcks } from './resync-acks'
import type {
  LagSignals,
  LifecycleSignals,
  RecoveryLevel,
  RecoveryReason,
  ResourceSignals,
  RestartResult,
  ResyncAckResult,
  ResyncDomain,
  RuntimeHealthAssessment,
  RuntimeSignals,
  UserWorkSignals,
  WebSocketHealth,
} from './types'

export type RecoveryDeps = {
  getHealth: () => WebSocketHealth
  restart: () => Promise<RestartResult>
  /** Bumps the generation AND triggerResyncAtom; must run before restart(). */
  beginResync: () => number
  queryClient: QueryClient
  /**
   * Domains expected to re-dump. With no active accounts none of the account
   * streams will ever send an initial dump, so waiting on them guarantees a
   * timeout — callers pass an empty list in that case.
   */
  getExpectedResyncDomains: () => readonly ResyncDomain[]
}

export type RecoveryController = {
  requestRecovery: (reason: RecoveryReason) => void
  attach: () => () => void
  detach: () => void
  /** Current state-machine position. For debugging and telemetry only. */
  getState: () => State
  /**
   * Logs a fresh signals snapshot without running a recovery cycle — for
   * inspecting the current score/heap-ratio/etc on demand (e.g. while sitting
   * on a tab that "feels" heavy) rather than only ever seeing them after a
   * visibility-return event. debug-mode/non-prod gated, same as the telemetry
   * this reuses assess()/collect() from.
   */
  logSnapshot: () => void
  /** Same data as logSnapshot, returned for the debug terminal. Null in prod. */
  getSnapshot: () => RuntimeHealthSnapshot | null
}

export type State = 'VISIBLE' | 'SUSPENDED' | 'ASSESSING' | 'LEVEL_1' | 'LEVEL_2' | 'LEVEL_3_RELOAD'

export type RuntimeHealthSnapshot = {
  state: State
  score: number
  recommendedLevel: RecoveryLevel
  safeToReload: boolean
  reloadBlockers: string[]
  assessmentReasons: string[]
  heapUsageRatio: number | null
  heapUsedMb: number | null
  heapLimitMb: number | null
  deviceMemoryGb: number | null
  socket: WebSocketHealth
  lifecycle: LifecycleSignals
  lag: LagSignals
  resources: ResourceSignals
  userWork: UserWorkSignals
}

export function createRecoveryController(deps: RecoveryDeps): RecoveryController {
  // Health getter is injected here rather than parked in a module global, so
  // two controllers can never clobber each other's during a remount.
  const collector = createSignalCollector(deps.getHealth)

  let state: State = 'VISIBLE'
  let attached = false
  /**
   * Bumped on every detach. Async continuations compare against it and bail, so
   * a restart or ack that resolves after teardown can't touch state or emit
   * telemetry.
   */
  let generation = 0
  let inFlight = false
  let lastAssessedAt = 0
  let lastResyncFailed = false

  /**
   * Reasons that arrived while a cycle was running. They are merged rather than
   * dropped: a `network-recovered` landing during a visibility-triggered cycle
   * is strictly stronger information, and discarding it would leave the
   * connection unrecovered.
   */
  let pendingReasons = new Set<RecoveryReason>()

  const timers = new Set<ReturnType<typeof setTimeout>>()
  /** Pending wake-up for reasons that arrived inside the dedupe window. */
  let deferredDrainId: ReturnType<typeof setTimeout> | null = null

  const schedule = (fn: () => void, delayMs: number) => {
    const id = setTimeout(() => {
      timers.delete(id)
      fn()
    }, delayMs)
    timers.add(id)
    return id
  }

  const clearTimers = () => {
    timers.forEach((id) => clearTimeout(id))
    timers.clear()
    deferredDrainId = null
  }

  // ─── Level 1 ──────────────────────────────────────────────────────────────

  type Level1Result = {
    restartResult: RestartResult
    ackResult: ResyncAckResult
    durationMs: number
    activeQueriesBeforeInvalidate: number
    invalidateDurationMs: number
  }

  async function runLevel1(): Promise<Level1Result> {
    const startedAt = Date.now()

    // ORDER MATTERS. beginResync() must bump the generation before the socket
    // reconnects: if restart() re-authenticates first, the server's fresh dump
    // arrives while the trackers still hold the old generation, gets classified
    // as a delta, and the ack never fires.
    const resyncGeneration = deps.beginResync()

    // Start the ack clock now rather than after restart() settles, so the
    // restart timeout and the ack timeout overlap instead of stacking.
    const ackPromise = waitForResyncAcks(
      resyncGeneration,
      RESYNC_ACK_TIMEOUT_MS,
      deps.getExpectedResyncDomains()
    )

    const activeQueriesBeforeInvalidate = deps.queryClient
      .getQueryCache()
      .getAll()
      .filter((query) => query.isActive()).length
    const invalidateStartedAt = Date.now()

    // Restart unconditionally once Level 1 is chosen. Skipping it when the
    // socket still reports ready looks tempting after a long background stint,
    // but only a fresh subscribe makes the server re-send initial dumps —
    // bumping the generation locally without one resets our trackers and then
    // waits for data that is never coming.
    const [restartResult] = await Promise.all([
      deps.restart(),
      // Keep live/snapshot data recoverable without refetching stable catalogs,
      // account/settings metadata, or AI/chat state that a socket restart cannot
      // make stale. snapshotAndUpdateQueryOptions queries remain included.
      deps.queryClient.invalidateQueries({
        predicate: (query) => shouldInvalidateForRuntimeRecovery(query.queryKey),
      }),
    ])

    const invalidateDurationMs = Date.now() - invalidateStartedAt
    const ackResult = await ackPromise

    return {
      restartResult,
      ackResult,
      durationMs: Date.now() - startedAt,
      activeQueriesBeforeInvalidate,
      invalidateDurationMs,
    }
  }

  // ─── Level 2 ──────────────────────────────────────────────────────────────

  type Level2Result = {
    removedInactiveQueries: number
    marketDataKeysFlushed: number
  }

  /**
   * Trims resources that are safe to drop without asking anything to
   * re-render or re-fetch on its own: queries with no active observer (a
   * remount just refetches), and market-data cache entries that already hit
   * zero ref-count and are only waiting out their grace period.
   *
   * Deliberately does NOT unmount live widgets or force a TradingView
   * remount, per docs §6 step 3-4 — safely tearing down a mounted widget from
   * outside React without losing in-progress state (drawings, scroll
   * position, filters) needs its own architecture work, not a blind call from
   * here. That stays a documented gap rather than a rushed implementation.
   */
  async function runLevel2(): Promise<Level2Result> {
    const cache = deps.queryClient.getQueryCache()
    const inactiveQueries = cache.getAll().filter((query) => !query.isActive())
    inactiveQueries.forEach((query) => cache.remove(query))

    const marketDataKeysFlushed = flushMarketDataCleanup()

    return { removedInactiveQueries: inactiveQueries.length, marketDataKeysFlushed }
  }

  // ─── Level 3 ──────────────────────────────────────────────────────────────

  /**
   * Reloads immediately — no countdown, no confirmation. Safe specifically
   * because a cycle only ever runs off a return-type event: the tab has to
   * have been away (or the socket to have just come back) for Level 3 to even
   * be reachable, which rules out an order or edit genuinely in flight right
   * now. The one thing worth re-checking is whether one landed in the brief
   * gap between the decision and this running — see getReloadBlockersNow().
   *
   * Fire-and-forget from runCycle's own return so a slow config flush doesn't
   * hold the controller from handling the next lifecycle event, but every
   * await here re-checks myGeneration before touching shared state.
   */
  async function runLevel3(myGeneration: number): Promise<void> {
    // Durability first: whatever this reload is about to discard from memory
    // must already be on the server before we act on it.
    await flushAllPendingWidgetConfigs().catch(() => {})
    if (myGeneration !== generation) return

    const blockers = getReloadBlockersNow()
    if (blockers.length > 0) {
      reportLevel3Outcome('blocked-unsafe')
      return
    }

    recordReloadAttempt()
    state = 'LEVEL_3_RELOAD'
    reportLevel3Outcome('reloaded')
    window.location.reload()
  }

  // ─── Assessment cycle ─────────────────────────────────────────────────────

  async function runCycle(reasons: RecoveryReason[]): Promise<void> {
    const myGeneration = generation
    const cycleStartedAt = Date.now()

    // First pass: lifecycle + socket state only. Waiting for the lag sampling
    // window here would delay reconnecting a dead socket by three seconds.
    const firstSignals = collector.collect(reasons, lastResyncFailed)
    const firstAssessment = assess(firstSignals)

    // Debug-mode hold: assess, record, execute nothing. The reload-blocker
    // registry only guards Level 3; Level 1 restarts the socket and Level 2
    // flushes caches, and either would reset the counters the debug terminal
    // is there to watch.
    if (shouldHoldForDebug(reasons, isTrackingActive())) {
      recordTrackerEvent({
        kind: 'recovery-suppressed',
        detail: {
          reasons,
          recommendedLevel: firstAssessment.recommendedLevel,
          score: firstAssessment.score,
          hiddenDurationMs: firstSignals.lifecycle.hiddenDurationMs,
          executionGapMs: firstSignals.lifecycle.executionGapMs,
          wasSuspended: firstSignals.lifecycle.wasSuspended,
          reconnectCount: firstSignals.socket.reconnectCount,
        },
      })
      state = 'VISIBLE'
      return
    }

    const longAbsence =
      firstSignals.lifecycle.hiddenDurationMs >= GLOBAL_VISIBILITY_RESYNC_THRESHOLD_MS

    // Fast path: skip Level 1 entirely and reload right away when either (a)
    // the absence alone is long enough (≥1hr hidden) to justify it
    // regardless of score, or (b) the first-pass signals already land in
    // Level 3 territory (recommendedLevel >= 2) before Level 1 even runs —
    // e.g. heap/resource budget blown, or a lifecycle gap, none of which a
    // socket restart fixes. Running Level 1 first in either case is wasted
    // latency, not a missing safety step, since we're about to discard the
    // whole page anyway — worse, it risks overlapping with anything else
    // that restarts the socket around the same time (the auth-transition
    // effect in websocket-context.tsx), which is what produces the server's
    // "Already Authenticated" error; that error is harmless on its own, but
    // seeing it means Level 1 ran and raced another restart for nothing.
    // Falls through to the normal graduated path (Level 1 → assess →
    // Level 2/3) whenever something blocks an immediate reload, so these
    // cases still get recovered even when they can't be fast-reloaded.
    const reloadWarrantedUpfront = longAbsence || firstAssessment.recommendedLevel >= 2
    if (reloadWarrantedUpfront && ENABLE_LEVEL_3 && getReloadBlockersNow().length === 0) {
      if (canAttemptReload()) {
        state = 'LEVEL_3_RELOAD'
        await runLevel3(myGeneration)
        return
      }
      reportLevel3Outcome('blocked-cooldown')
    }

    collector.startLagSampling()

    // A completed token probe means the network genuinely came back and the
    // socket was reconnected underneath us, so Level 1 runs regardless of score.
    // The bare browser `online` event is not that — it only prompts a re-assess.
    const networkRecovered = reasons.includes('network-recovered')
    const needsLevel1 =
      networkRecovered ||
      firstAssessment.recommendedLevel >= 1 ||
      shouldForceLevel1(firstSignals, longAbsence)

    let executedLevel: RecoveryLevel = 0
    let restartResult: RestartResult | null = null
    let ackResult: ResyncAckResult | null = null
    let level1DurationMs: number | null = null
    let activeQueriesBeforeInvalidate = 0
    let invalidateDurationMs = 0

    if (needsLevel1) {
      state = 'LEVEL_1'
      executedLevel = 1
      const result = await runLevel1()
      if (myGeneration !== generation) return

      restartResult = result.restartResult
      ackResult = result.ackResult
      level1DurationMs = result.durationMs
      activeQueriesBeforeInvalidate = result.activeQueriesBeforeInvalidate
      invalidateDurationMs = result.invalidateDurationMs
      lastResyncFailed = result.ackResult !== 'ok' || result.restartResult !== 'ready'
      if (isBackendStreamFailure(result.restartResult, result.ackResult)) {
        // Transport and authentication recovered, but the expected server dumps never
        // arrived. A page reload would reconnect to the same unhealthy backend stream,
        // so open a short circuit instead of escalating into a reload loop.
        recordBackendStreamFailure()
      } else if (result.restartResult === 'ready' && result.ackResult === 'ok') {
        clearBackendStreamFailure()
      }
    } else {
      lastResyncFailed = false
    }

    // Single-pass decision: re-collect immediately after Level 1 settles (or
    // right away, if Level 1 didn't run) instead of waiting out a fixed
    // sampling window for a second opinion. That artificial wait landed
    // squarely in the first few seconds after the user returns — exactly
    // when they're most likely mid-click (opening an order form, switching
    // tabs) — so it only widened the chance recovery ambushed them
    // mid-interaction. Level 1's own real latency (when it runs) still
    // serves as the lag-sampling window; skipping Level 1 means skipping
    // that window too, but that only happens when the first pass already
    // looked healthy.
    collector.stopLagSampling()
    const secondSignals = collector.collect(reasons, lastResyncFailed)
    const secondAssessment = assess(secondSignals)

    // Re-hidden mid-recovery: record the outcome but never promote. Escalating
    // for a tab the user isn't looking at spends resources for no benefit, and
    // Level 3 would reload a page they can't see.
    const wentHiddenAgain =
      typeof document !== 'undefined' && document.visibilityState !== 'visible'

    let finalSignals = secondSignals
    let finalAssessment = secondAssessment
    let level2Result: Level2Result | null = null

    if (ENABLE_LEVEL_2 && !wentHiddenAgain && secondAssessment.recommendedLevel >= 2) {
      state = 'LEVEL_2'
      level2Result = await runLevel2()
      if (myGeneration !== generation) return

      // Re-measure rather than trust the pre-cleanup score — Level 2 exists
      // specifically to change this number.
      finalSignals = collector.collect(reasons, lastResyncFailed)
      finalAssessment = assess(finalSignals)
    }

    const shadowLevel = wentHiddenAgain ? null : finalAssessment.recommendedLevel
    // NOT gated on lastResyncFailed: a successful Level 1 reconnect fixes the
    // socket, but does nothing about resources that piled up over a long
    // absence (heap, query/widget counts, lifecycle gap). A resync failure
    // still pushes the score up via SCORE_RESYNC_FAILED, it just isn't a
    // required precondition any more.
    const level3Candidate =
      !wentHiddenAgain && finalAssessment.recommendedLevel >= 2 && finalAssessment.safeToReload

    emitTelemetry({
      signals: finalSignals,
      firstAssessment,
      secondAssessment: finalAssessment,
      reasons,
      executedLevel,
      shadowLevel,
      level3Candidate,
      restartResult,
      ackResult,
      level1DurationMs,
      totalDurationMs: Date.now() - cycleStartedAt,
      activeQueriesBeforeInvalidate,
      invalidateDurationMs,
      level2Result,
    })

    state = wentHiddenAgain ? 'SUSPENDED' : 'VISIBLE'

    if (ENABLE_LEVEL_3 && level3Candidate) {
      if (lastResyncFailed && isBackendStreamReloadSuppressed()) {
        reportLevel3Outcome('blocked-backend-stream')
      } else if (canAttemptReload()) {
        // Fire-and-forget: runLevel3 flushes pending config then reloads
        // immediately, no confirmation step. See runLevel3's doc comment for
        // why that's safe here specifically.
        void runLevel3(myGeneration)
      } else {
        reportLevel3Outcome('blocked-cooldown')
      }
    }
  }

  function emitTelemetry(input: {
    signals: RuntimeSignals
    firstAssessment: RuntimeHealthAssessment
    secondAssessment: RuntimeHealthAssessment
    reasons: RecoveryReason[]
    executedLevel: RecoveryLevel
    shadowLevel: RecoveryLevel | null
    level3Candidate: boolean
    restartResult: RestartResult | null
    ackResult: ResyncAckResult | null
    level1DurationMs: number | null
    totalDurationMs: number
    activeQueriesBeforeInvalidate: number
    invalidateDurationMs: number
    level2Result: Level2Result | null
  }): void {
    const { signals, secondAssessment } = input
    const now = signals.collectedAt
    const queries = deps.queryClient.getQueryCache().getAll()

    const ageOf = (at: number | null) => (at === null ? null : now - at)

    reportRecovery({
      ...environmentFields(signals.memory.deviceMemoryGb),

      reasons: input.reasons,
      hiddenDurationMs: signals.lifecycle.hiddenDurationMs,
      executionGapMs: signals.lifecycle.executionGapMs,
      wasRestoredFromBFCache: signals.lifecycle.wasRestoredFromBFCache,
      wasDiscarded: signals.lifecycle.wasDiscarded,

      memoryApiSupported: signals.memory.supported,
      heapUsageRatio: signals.memory.heapUsageRatio,

      lagApiSupported: signals.lag.supported,
      lagEntryType: signals.lag.entryType,
      lagSampleCount: signals.lag.sampleCount,
      lagTotalDurationMs: signals.lag.totalDurationMs,
      lagMaxDurationMs: signals.lag.maxDurationMs,
      lagSevereCount: signals.lag.severeCount,

      socketIsReady: signals.socket.isReady,
      transportAgeMs: ageOf(signals.socket.lastTransportMessageAt),
      heartbeatAgeMs: ageOf(signals.socket.lastHeartbeatAt),
      dataAgeMs: ageOf(signals.socket.lastDataMessageAt),
      reconnectCount: signals.socket.reconnectCount,
      activeSubscriptionCount: signals.socket.activeSubscriptionCount,

      totalQueryCount: signals.resources.totalQueryCount,
      inactiveQueryCount: signals.resources.inactiveQueryCount,
      configuredWidgetCount: signals.resources.configuredWidgetCount,
      mountedWidgetCount: signals.resources.mountedWidgetCount,
      tradingViewContainerCount: signals.resources.tradingViewContainerCount,

      activeQueriesBeforeInvalidate: input.activeQueriesBeforeInvalidate,
      queriesFetchingAfterInvalidate: queries.filter((q) => q.state.fetchStatus === 'fetching')
        .length,
      invalidateDurationMs: input.invalidateDurationMs,
      failedRefetchCount: queries.filter((q) => q.state.status === 'error').length,

      score: secondAssessment.score,
      executedLevel: input.executedLevel,
      recommendedLevel: secondAssessment.recommendedLevel,
      shadowLevel: input.shadowLevel,
      level3Candidate: input.level3Candidate,
      restartResult: input.restartResult,
      ackResult: input.ackResult,
      level1DurationMs: input.level1DurationMs,
      totalDurationMs: input.totalDurationMs,
      succeeded: input.executedLevel === 0 || !lastResyncFailed,
      safeToReload: secondAssessment.safeToReload,
      reloadBlockers: secondAssessment.reloadBlockers,
      assessmentReasons: secondAssessment.reasons,

      level2Executed: input.level2Result !== null,
      removedInactiveQueries: input.level2Result?.removedInactiveQueries ?? null,
      marketDataKeysFlushed: input.level2Result?.marketDataKeysFlushed ?? null,
    })
  }

  // ─── Entry point ──────────────────────────────────────────────────────────

  function requestRecovery(reason: RecoveryReason): void {
    if (!attached) return

    // While degraded, useConnectionRecovery's token probe owns reconnection.
    // Restarting alongside it would race its connect() and scramble the auth
    // and resubscribe ordering. It hands control back with 'network-recovered'
    // once the probe succeeds.
    if (reason === 'browser-online' && store.get(connectionStatusAtom) !== 'online') return

    pendingReasons.add(reason)

    // A cycle is running; it drains whatever accumulated when it finishes.
    if (inFlight) return

    const sinceLastAssessment = Date.now() - lastAssessedAt
    if (sinceLastAssessment < RECOVERY_DEDUPE_MS) {
      // A minimum re-assessment interval, not a request filter. The reason is
      // already merged above, but something has to come back for it — without
      // this the queued reason would sit there until an unrelated event
      // happened to arrive outside the window.
      if (deferredDrainId === null) {
        deferredDrainId = schedule(() => {
          deferredDrainId = null
          void drain()
        }, RECOVERY_DEDUPE_MS - sinceLastAssessment)
      }
      return
    }

    void drain()
  }

  async function drain(): Promise<void> {
    if (inFlight) return
    inFlight = true

    try {
      while (pendingReasons.size > 0) {
        if (!attached) return
        const reasons = Array.from(pendingReasons)
        pendingReasons = new Set()
        lastAssessedAt = Date.now()
        state = 'ASSESSING'
        await runCycle(reasons)
        if (!attached) return
      }
    } finally {
      inFlight = false
    }
  }

  // ─── Lifecycle wiring ─────────────────────────────────────────────────────

  function attach(): () => void {
    attached = true

    const abortController = new AbortController()
    const { signal } = abortController
    const on = (target: Window | Document, type: string, handler: (event: Event) => void) =>
      target.addEventListener(type, handler, { signal })

    on(document, 'visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        state = 'SUSPENDED'
        collector.markSuspended()
        return
      }
      collector.markReturned({})
      requestRecovery('visibility-return')
    })

    // Page Lifecycle API. Both are Chromium-only, hence the pagehide fallback
    // below — without it a Safari/Firefox tab that goes away never records a
    // suspension and its execution gap is misattributed.
    on(document, 'freeze', () => {
      state = 'SUSPENDED'
      collector.markSuspended()
    })
    on(document, 'resume', () => {
      collector.markReturned({})
      requestRecovery('resume')
    })
    on(window, 'pagehide', () => {
      state = 'SUSPENDED'
      collector.markSuspended()
    })
    on(window, 'pageshow', (event) => {
      const persisted = (event as PageTransitionEvent).persisted === true
      collector.markReturned({ restoredFromBFCache: persisted })
      requestRecovery('pageshow')
    })
    on(window, 'online', () => requestRecovery('browser-online'))

    return () => {
      abortController.abort()
      detach()
    }
  }

  function detach(): void {
    attached = false
    // Invalidate every in-flight continuation before tearing anything down.
    generation += 1
    inFlight = false
    pendingReasons = new Set()
    clearTimers()
    collector.stopLagSampling()
    collector.dispose()
  }

  function getSnapshot(): RuntimeHealthSnapshot | null {
    if (env.NEXT_PUBLIC_ENV === 'prod') return null
    const signals = collector.collect([], lastResyncFailed)
    const assessment = assess(signals)
    return {
      state,
      score: assessment.score,
      recommendedLevel: assessment.recommendedLevel,
      safeToReload: assessment.safeToReload,
      reloadBlockers: assessment.reloadBlockers,
      assessmentReasons: assessment.reasons,
      heapUsageRatio: signals.memory.heapUsageRatio,
      heapUsedMb: signals.memory.usedHeapBytes === null ? null : signals.memory.usedHeapBytes / 1e6,
      heapLimitMb:
        signals.memory.heapLimitBytes === null ? null : signals.memory.heapLimitBytes / 1e6,
      deviceMemoryGb: signals.memory.deviceMemoryGb,
      socket: signals.socket,
      lifecycle: signals.lifecycle,
      lag: signals.lag,
      resources: signals.resources,
      userWork: signals.userWork,
    }
  }

  function logSnapshot(): void {
    if (!store.get(debugModeAtom)) return
    const snapshot = getSnapshot()
    if (snapshot) console.info('[runtime-health] snapshot', snapshot)
  }

  return { requestRecovery, attach, detach, getState: () => state, logSnapshot, getSnapshot }
}
